import _ from 'underscore';
import {formEncodedApi} from 'nutshell-core/api';
import {joyrideSteps, PERSON_PLACEHOLDER, LEAD_PLACEHOLDER} from './onboarding-constants';

// These settings constants can be found in Nut_OnboardingFlags.php
const ONBOARDING_JOYRIDE_ACTIVE_JOYRIDE_ID = 'onboarding_joyride_active_id';
const ONBOARDING_JOYRIDE_CURRENT_STEP_INDEX_SETTING_PREFIX =
    'onboarding_joyride_current_step_index_';

/**
 * Persist a joyrideId's stepIndex to the database. Setting key is preceded with
 * the joyrideId.
 * @param  {string} joyrideId   Joyride id to update the setting for
 * @param  {integer} stepIndex  stepIndex to persist. This should be the _new_ currentStepIndex
 *                              that would be started if the page was refreshed
 * @return {Promise}            Promise to hook into and continue whatever we're doing
 */
export function persistNewJoyrideStepIndex(joyrideId, stepIndex) {
    if (isNaN(stepIndex)) {
        return Promise.reject(new Error('Invalid stepIndex'));
    }

    return formEncodedApi.post('/my-account/save-user-setting.json', {
        setting: `${ONBOARDING_JOYRIDE_CURRENT_STEP_INDEX_SETTING_PREFIX}${joyrideId}`,
        value: stepIndex,
    });
}

/**
 * Persist the active joyrideId to the database in its own setting. This allows us
 * to start joyrides on refresh, assuming this value is non-null.
 *
 * @param  {string} joyrideId   Joyride id to update the setting for
 *
 * @return {Promise}            Promise to hook into and continue whatever we're doing
 */
export function persistJoyrideActiveId(joyrideId) {
    return formEncodedApi.post('/my-account/save-user-setting.json', {
        setting: ONBOARDING_JOYRIDE_ACTIVE_JOYRIDE_ID,
        value: joyrideId ? joyrideId : 0,
    });
}

function getCurrentPathname() {
    return window.location.pathname;
}

export function checkRedirectNecessary(stepPathname) {
    const currentPathname = getCurrentPathname();
    const createdPersonId = NutClientConfig.onboardingJoyrideCreatedPersonId;
    const createdLeadId = NutClientConfig.onboardingJoyrideCreatedLeadId;
    const hasPersonPlaceholder = checkForPersonPlaceholder(stepPathname);
    const hasLeadPlaceholder = checkForLeadPlaceholder(stepPathname);
    const currentPeep =
        typeof window.Peep !== 'undefined' &&
        window.Peep.currentPeep &&
        window.Peep.currentPeep.attributes;
    const currentLead = typeof window.currentLead !== 'undefined' && window.currentLead;
    const onPersonPage = currentPeep && currentPeep.type === 'contacts';

    if (!stepPathname) {
        return false;
    } else if (hasPersonPlaceholder) {
        return !onPersonPage || currentPeep.id !== createdPersonId;
    } else if (hasLeadPlaceholder) {
        return !currentLead || currentLead.id !== createdLeadId;
    } else {
        return currentPathname !== stepPathname;
    }
}

export function goToPath(stepUri) {
    let pathname = stepUri;
    if (checkForPersonPlaceholder(stepUri)) {
        pathname = getDynamicPersonPathname(stepUri);
    } else if (checkForLeadPlaceholder(stepUri)) {
        pathname = getDynamicLeadPathname(stepUri);
    }

    const protocol = window.location.protocol;
    const host = window.location.host;
    window.location.assign(`${protocol}//${host}${pathname}`);
}

export function checkForPersonPlaceholder(stepUri) {
    return stepUri && stepUri.endsWith(PERSON_PLACEHOLDER);
}

export function checkForLeadPlaceholder(stepUri) {
    return stepUri && stepUri.endsWith(LEAD_PLACEHOLDER);
}

export function getDynamicPersonPathname(stepUri) {
    let personId = '1-contacts'; // Fallback
    if (
        typeof NutClientConfig !== 'undefined' &&
        NutClientConfig.onboardingJoyrideCreatedPersonId
    ) {
        personId = NutClientConfig.onboardingJoyrideCreatedPersonId;
    }

    return stepUri.replace(PERSON_PLACEHOLDER, personId);
}

export function getDynamicLeadPathname(stepUri) {
    let leadId = '1-leads'; // Fallback
    if (typeof NutClientConfig !== 'undefined' && NutClientConfig.onboardingJoyrideCreatedLeadId) {
        leadId = NutClientConfig.onboardingJoyrideCreatedLeadId;
    }

    return stepUri.replace(LEAD_PLACEHOLDER, leadId);
}

export function findResumableStepIndex(joyrideId, index) {
    const steps = joyrideSteps[joyrideId];
    const earlierSteps = steps.slice(0, index + 1);

    const resumableStepIndex = _.findLastIndex(earlierSteps, (item) => {
        return item.isResumable !== false;
    });

    return resumableStepIndex >= 0 ? resumableStepIndex : 0;
}
